import { useState } from 'react';
import { supabase } from '../../lib/supabase.ts';
import { useOnline } from '../../lib/use-online.ts';
import { Alert, Button, Input } from '../../ui/index.tsx';

/**
 * «¿Olvidaste tu contraseña?» (dueños): manda un email con un link para elegir una nueva. El link
 * abre la app en /definir-clave. Con el email de Supabase del plan gratis se pueden mandar pocos
 * por hora; si se pasa, se avisa que pida una contraseña provisoria a soporte.
 */
export function ForgotPassword({ email: initialEmail }: { email?: string }) {
  const online = useOnline();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(initialEmail ?? '');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  if (!open) {
    return (
      <button type="button" className="block w-full text-center text-sm font-medium text-brand underline" onClick={() => setOpen(true)}>
        ¿Olvidaste tu contraseña?
      </button>
    );
  }

  const send = async () => {
    const value = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) return setResult({ tone: 'error', text: 'Escribí tu email.' });
    setBusy(true);
    setResult(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(value, { redirectTo: `${window.location.origin}/definir-clave` });
      if (error) {
        const tooMany = error.status === 429 || /rate limit|too many/i.test(error.message);
        setResult({
          tone: 'error',
          text: tooMany
            ? 'Se mandaron demasiados emails en poco tiempo. Probá de nuevo en una hora, o pedile a soporte una contraseña provisoria.'
            : `No se pudo mandar el email: ${error.message}`,
        });
      } else {
        setResult({
          tone: 'success',
          text: 'Listo: si ese email tiene una cuenta, te llegó un mensaje con un link para elegir una contraseña nueva (mirá también en spam). El link vence en una hora.',
        });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-lg bg-slate-50 p-3 text-sm">
      <p className="font-medium">Recuperar la contraseña</p>
      <Input type="email" autoComplete="email" placeholder="Tu email" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email" />
      {result && <Alert tone={result.tone}>{result.text}</Alert>}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
          Cancelar
        </Button>
        <Button size="sm" loading={busy} disabled={!online} onClick={() => void send()}>
          Mandarme el link
        </Button>
      </div>
      {!online && <p className="text-xs text-amber-700">Necesita conexión a internet.</p>}
    </div>
  );
}
